import { useState, useCallback } from 'react';
import {
    Button,
    Card,
    Center,
    Stack,
    Text,
    Title,
    ThemeIcon,
    Group,
} from '@mantine/core';
import { Icon } from '@iconify/react';
import { useActions } from 'src/state';

type LogoutStatus = 'idle' | 'loading' | 'success';

export default function Logout() {

    const signOutAction = useActions().signOutAction;
    const [status, setStatus] = useState<LogoutStatus>('idle');

    const handleLogout = useCallback(async () => {

        setStatus('loading');
        await signOutAction();
        setStatus('success');
        
    }, [signOutAction]);

    const handleCancel = useCallback(() => {
        window.history.back();
    }, []);

    const getIconConfig = () => {
        switch (status) {
            case 'success':
                return { icon: 'mdi:check-circle', color: 'green' };
            case 'loading':
                return { icon: 'mdi:logout', color: 'blue' };
            default:
                return { icon: 'mdi:logout', color: 'blue' };
        }
    };

    const { icon, color } = getIconConfig();

    return (
        <Center pt={100} p="md">
            <Card withBorder shadow="md" p="xl" radius="md" w={400}>
                <Stack gap="lg" align="center">
                    {/* Icon */}
                    <ThemeIcon
                        size={80}
                        radius="xl"
                        variant="light"
                        color={color}
                    >
                        <Icon icon={icon} width={40} height={40} />
                    </ThemeIcon>

                    {/* Title */}
                    <Title order={2} ta="center">
                        {status === 'success'
                            ? 'Signed Out'
                            : status === 'loading'
                              ? 'Signing Out...'
                              : 'Sign Out'}
                    </Title>

                    {/* Description */}
                    <Text c="dimmed" ta="center" size="sm">
                        {status === 'success' ? (
                            <>
                                You have been signed out successfully.
                                <br />
                                Redirecting...
                            </>
                        ) : status === 'loading' ? (
                            'Please wait while we sign you out...'
                        ) : (
                            <>
                                Are you sure you want to sign out?
                                <br />
                                You'll need to sign in again to access your account.
                            </>
                        )}
                    </Text>

                    {/* Actions */}
                    <Stack gap="sm" w="100%" mt="md">
                        {status === 'idle' && (
                            <>
                                <Button
                                    fullWidth
                                    color="red"
                                    leftSection={<Icon icon="mdi:logout" width={18} />}
                                    onClick={handleLogout}
                                >
                                    Yes, Sign Out
                                </Button>
                                <Button
                                    fullWidth
                                    variant="light"
                                    color="gray"
                                    leftSection={<Icon icon="mdi:arrow-left" width={18} />}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                        {status === 'loading' && (
                            <Button fullWidth loading disabled>
                                Signing out...
                            </Button>
                        )}
                        {status === 'success' && (
                            <Group justify="center">
                                <Icon icon="mdi:loading" width={24} className="mantine-rotate" />
                            </Group>
                        )}
                    </Stack>
                </Stack>
            </Card>
        </Center>
    );
}
